import { useState } from "react";
import { ArrowDown, XCircle, Sparkles, Bookmark, BookmarkCheck } from "lucide-react";
import { copingStyleLabel, needLabel, schemaScript } from "../lib/schemaFlip";
import { aiPost, useCards, useToggleCard } from "../lib/store";
import { Spinner } from "./Brand";
import { toast } from "sonner";
import { FeedbackBar, NoteHeader } from "./AiNote";

export function SaveCardButton({ schema, title, text, testId }) {
  const { data: cards } = useCards();
  const toggle = useToggleCard();
  const saved = (cards ?? []).some((c) => c.text === text);
  const onClick = async () => {
    const r = await toggle.mutateAsync({ schema, title, text });
    toast.success(r.saved ? "Saved to your script cards" : "Removed from your cards");
  };
  return (
    <button type="button" onClick={onClick} data-testid={testId}
      className={`self-start inline-flex items-center gap-1.5 rounded-full px-3.5 h-9 text-[13px] font-semibold transition-colors ${saved ? "bg-t-brand text-t-onbrand" : "bg-t-brand2 text-t-brand"}`}>
      {saved ? <BookmarkCheck size={15} /> : <Bookmark size={15} />} {saved ? "Saved as card" : "Save as card"}
    </button>
  );
}

function FlipCoach({ flip, adaptive, needs, onReply, index }) {
  const [loading, setLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const ready = !!(flip.story.trim() || flip.facts.trim());
  const ask = async () => {
    setLoading(true);
    setFailed(false);
    const reply = await aiPost("flip-coach", {
      schema_name: flip.schema, adaptive, copingStyle: copingStyleLabel(flip.copingStyle),
      coreNeed: needs.filter((n) => n.includes(":")).map(needLabel).join("; ") || null,
      story: flip.story.trim(), facts: flip.facts.trim(), action: flip.action.trim(),
    }, "reply");
    setLoading(false);
    if (reply) onReply(reply);
    else setFailed(true);
  };
  if (flip.coach) {
    return (
      <div className="rounded-2xl bg-t-brand3 border border-t-brand2 p-4 flex flex-col gap-2" data-testid={`flip-coach-reply-${index}`}>
        <NoteHeader title={<span className="flex items-center gap-1.5 tf-tag"><Sparkles size={14} /> Your Flip Coach</span>} text={flip.coach} testId={`flip-coach-listen-${index}`} />
        <p className="text-[15px] text-t-on leading-relaxed whitespace-pre-line">{flip.coach}</p>
        <div className="flex items-center gap-3 flex-wrap">
          <SaveCardButton schema={flip.schema} title="Flip Coach" text={flip.coach} testId={`flip-coach-save-${index}`} />
          <button onClick={ask} className="text-sm text-t-brand font-semibold" data-testid={`flip-coach-again-${index}`}>{loading ? "Thinking…" : "Ask again"}</button>
        </div>
        <FeedbackBar key={flip.coach} kind="flip-coach" testId={`flip-coach-feedback-${index}`} />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      <button type="button" onClick={ask} disabled={!ready || loading} data-testid={`flip-coach-ask-${index}`}
        className="self-start inline-flex items-center gap-2 rounded-full bg-t-brand text-t-onbrand px-5 h-11 font-semibold disabled:opacity-50">
        {loading ? <Spinner className="w-4 h-4" /> : <Sparkles size={16} />} {loading ? "Your coach is reflecting…" : "Ask my Flip Coach"}
      </button>
      {!ready ? <p className="text-[13px] text-t-muted">Write the story or the facts first, so your coach can respond to them.</p> : null}
      {failed ? <p className="text-[13px] text-t-muted">Your coach couldn't be reached. Please try again in a moment.</p> : null}
    </div>
  );
}

const Label = ({ children }) => <p className="text-[13px] font-bold uppercase tracking-wider text-t-brand mt-2">{children}</p>;

export function FlipPlan({ flips, onChange, mode, needs = [] }) {
  const patch = (i, p) => onChange(flips.map((f, j) => (j === i ? { ...f, ...p } : f)));
  return (
    <div className="flex flex-col gap-5 mt-4">
      {flips.map((f, i) => {
        const sc = schemaScript(f.schema);
        if (!sc) return null;
        const style = copingStyleLabel(f.copingStyle);
        return (
          <div key={f.schema} className="tf-card p-5 flex flex-col gap-3" data-testid={`flip-card-${i}`}>
            <Label>1 · Name it</Label>
            <h3 className="text-xl font-bold text-t-on">{f.schema}</h3>
            {style || mode ? <p className="text-[13px] text-t-muted">{[style && `Coping style: ${style}`, mode && `Mode: ${mode}`].filter(Boolean).join("  ·  ")}</p> : null}
            <div className="flex flex-col gap-2 rounded-2xl bg-t-surface p-4 border border-t-border">
              <div className="flex items-center gap-3 rounded-xl border p-3" style={{ background: "color-mix(in srgb, var(--hot) 14%, transparent)", borderColor: "var(--hot)" }}>
                <p className="flex-1 italic font-medium" style={{ color: "var(--hot)" }}>“{sc.script}”</p>
                <XCircle size={28} style={{ color: "var(--hot)" }} />
              </div>
              <ArrowDown size={18} className="self-center text-t-brand" />
              <p className="font-bold text-t-brand">{sc.adaptive}</p>
              <p className="text-[17px] font-semibold text-t-on">“{sc.adaptiveScript}”</p>
              <SaveCardButton schema={f.schema} title={sc.adaptive} text={sc.adaptiveScript} testId={`flip-save-card-${i}`} />
            </div>
            <Label>Or write my own adaptive script</Label>
            <textarea className="tf-input" value={f.ownScript ?? ""} onChange={(e) => patch(i, { ownScript: e.target.value })}
              placeholder="In my own words, my Healthy Adult says…" data-testid={`flip-own-script-${i}`} />
            {f.ownScript?.trim() ? <SaveCardButton schema={f.schema} title="My own script" text={f.ownScript.trim()} testId={`flip-own-save-${i}`} /> : null}
            <p className="text-[15px] text-t-on2">{sc.flip}</p>
            <Label>2 · Reality test</Label>
            <textarea className="tf-input" value={f.story} onChange={(e) => patch(i, { story: e.target.value })} placeholder="The story my schema is telling me…" data-testid={`flip-story-${i}`} />
            <textarea className="tf-input" value={f.facts} onChange={(e) => patch(i, { facts: e.target.value })} placeholder="The facts — what actually happened?" data-testid={`flip-facts-${i}`} />
            <FlipCoach flip={f} adaptive={sc.adaptive} needs={needs} onReply={(t) => patch(i, { coach: t })} index={i} />
            <Label>3 · Reparent your Vulnerable Child</Label>
            <textarea className="tf-input !bg-t-brand2" value={f.reparent} onChange={(e) => patch(i, { reparent: e.target.value })} data-testid={`flip-reparent-${i}`} />
            <Label>4 · One small opposite action</Label>
            <div className="flex flex-wrap gap-2">
              {sc.actions.map((a, k) => {
                const on = f.action === a;
                return (
                  <button key={a} type="button" onClick={() => patch(i, { action: on ? "" : a })} data-testid={`flip-action-${i}-${k}`}
                    className={`rounded-full border px-4 py-2 text-sm text-left transition-colors ${on ? "bg-t-brand text-t-onbrand border-t-brand" : "bg-t-surface text-t-on border-t-border"}`}>{a}</button>
                );
              })}
            </div>
            <input className="tf-input" value={sc.actions.includes(f.action) ? "" : f.action} onChange={(e) => patch(i, { action: e.target.value })}
              placeholder="…or write your own small, imperfect step" data-testid={`flip-action-custom-${i}`} />
          </div>
        );
      })}
    </div>
  );
}
